import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Search, Filter, Download, Clock, CheckCircle2, XCircle, ShieldAlert, Check, Banknote, Smartphone, CreditCard, Building, Target } from 'lucide-react';
import styles from './ExpenseTable.module.css';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateOf = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—');

const MODE_ICON = { Cash: Banknote, UPI: Smartphone, 'Credit Card': CreditCard };
const ModePill = ({ mode }) => {
    const Icon = MODE_ICON[mode] || Building;
    const cash = mode === 'Cash';
    return (
        <span className={`${styles.payModePill} ${cash ? styles.payCash : ''}`} title={cash ? 'Paid from the cash drawer' : undefined}>
            <Icon size={11} /> {mode || 'Not set'}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const config = {
        pending: { icon: Clock, cls: styles.badgePending, text: 'Pending' },
        approved: { icon: CheckCircle2, cls: styles.badgeApproved, text: 'Approved' },
        rejected: { icon: XCircle, cls: styles.badgeRejected, text: 'Rejected' },
    }[status] || { icon: Clock, cls: styles.badgePending, text: status || 'Pending' };
    const Icon = config.icon;
    return <span className={`${styles.statusBadge} ${config.cls}`}><Icon size={11} strokeWidth={2.5} /> {config.text}</span>;
};

const ExpenseTable = ({ expenses, canApprove, canExport = false, rangeLabel = '', onApprove, onReject }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const filteredExpenses = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return expenses.filter((exp) => {
            const text = [exp.desc, exp.expenseNo, exp._id, exp.category, exp.subcategory, exp.invoiceNo, exp.submitterName].join(' ').toLowerCase();
            return (!q || text.includes(q)) && (filterStatus === 'all' || exp.status === filterStatus);
        });
    }, [expenses, searchTerm, filterStatus]);

    const shownTotal = filteredExpenses.filter((e) => e.status !== 'rejected' && e.type !== 'Planned').reduce((s, e) => s + (Number(e.amount) || 0), 0);

    const exportExcel = () => {
        if (!filteredExpenses.length) { alert('No expenses to export.'); return; }
        const rows = filteredExpenses.map((e) => ({
            'Expense ID': e.expenseNo || e._id,
            Date: dateOf(e.date),
            Type: e.type,
            Category: e.category,
            'Sub-category': e.subcategory,
            Description: e.desc,
            'Invoice / Ref': e.invoiceNo || '',
            'Payment mode': e.payMode,
            Amount: Number(e.amount) || 0,
            Status: e.status,
            'Recorded by': e.submitterName || '',
            Remark: e.remark || '',
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Expenses');
        XLSX.writeFile(wb, `Expenses_${rangeLabel || 'export'}.xlsx`);
    };

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableToolbar}>
                <div className={styles.searchBox}>
                    <Search size={13} className={styles.searchIcon} />
                    <input type="text" placeholder="Search description, category, ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
                </div>
                <div className={styles.filterGroup}>
                    <span className={styles.countText}>{filteredExpenses.length} {filteredExpenses.length === 1 ? 'expense' : 'expenses'} · {inr(shownTotal)} spent</span>
                    <div className={styles.selectWrap}>
                        <Filter size={13} className={styles.filterIcon} />
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={styles.filterSelect}>
                            <option value="all">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    {canExport && <button type="button" className={styles.outlineBtn} onClick={exportExcel}><Download size={12} /> Export</button>}
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.expenseTable}>
                    <thead>
                        <tr>
                            <th>Exp ID & Date</th><th>Type & Category</th><th>Description</th><th>Payment Mode</th>
                            <th className={styles.textRight}>Amount</th><th>Status</th><th className={styles.textCenter}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredExpenses.map((exp) => (
                            <tr key={exp._id} className={exp.status === 'rejected' ? styles.rowRejected : exp.type === 'Planned' ? styles.rowPlanned : ''}>
                                <td>
                                    <div className={styles.tdId}>{exp.expenseNo || '—'}</div>
                                    <div className={styles.tdSubtext}>{dateOf(exp.date)}</div>
                                </td>
                                <td>
                                    <div className={styles.typeWrap}>
                                        <span className={exp.type === 'Planned' ? styles.typePlanned : styles.typeSettled}>
                                            {exp.type === 'Planned' ? <Target size={10} /> : <CheckCircle2 size={10} />} {exp.type === 'Planned' ? 'Planned' : 'Paid'}
                                        </span>
                                        <div className={styles.tdCategory}>{exp.category} {exp.subcategory && <span className={styles.tdSubCategory}>({exp.subcategory})</span>}</div>
                                    </div>
                                </td>
                                <td className={styles.tdDescWrap}>
                                    <div className={styles.tdDesc}>{exp.desc || '—'}</div>
                                    <div className={styles.tdSubtext}>Inv: {exp.invoiceNo || 'N/A'} · By: {exp.submitterName || 'Staff'}</div>
                                    {exp.status === 'rejected' && exp.remark && <div className={styles.remark}>Reason: {exp.remark}</div>}
                                </td>
                                <td><ModePill mode={exp.payMode} /></td>
                                <td className={`${styles.tdAmount} ${styles.textRight}`}>{inr(exp.amount)}</td>
                                <td><StatusBadge status={exp.status} /></td>
                                <td className={styles.textCenter}>
                                    {canApprove && exp.status === 'pending' ? (
                                        <div className={styles.actionGroup}>
                                            <button type="button" className={styles.btnApprove} onClick={() => onApprove(exp._id)} title="Approve" aria-label="Approve"><Check size={13} /></button>
                                            <button type="button" className={styles.btnReject} onClick={() => onReject(exp._id)} title="Reject" aria-label="Reject"><XCircle size={13} /></button>
                                        </div>
                                    ) : (
                                        <span className={styles.noActionText}>{exp.status === 'pending' ? <><ShieldAlert size={11} /> In review</> : '—'}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filteredExpenses.length === 0 && (
                            <tr><td colSpan="7" className={styles.emptyTable}>{expenses.length ? 'No expenses match your search.' : 'No expenses recorded for this period.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExpenseTable;
